import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import toast from "react-hot-toast";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const Toast = () => {
  const Success = (message, options = {}) => toast.success(message, options);
  const Error = (message, options = {}) => toast.error(message, options);
  const Info = (message, options = {}) =>
    toast.custom(
      <div
        style={{ background: "white", padding: "10px", borderRadius: "5px" }}
      >
        {message}
      </div>,
      options
    );
  const Loading = (message, options = {}) => toast.loading(message, options);

  return { Success, Error, Info, Loading };
};

export default Toast;

export const resendOtpToEmail = async (email) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/auth/send-otp`,
      {
        email,
      }
    );
    if (response) {
      Toast().Success("Rent OTP");
    }
  } catch (error) {
    Toast().Error(
      error?.response?.data?.message || error?.response?.data?.errors?.[0]?.msg
    );
  }
};

export const getTimezoneFromRegion = (region) => {
  const timezoneToRegion = {
    // North America
    'America/New_York': 'US-East',
    'America/Chicago': 'US-Central',
    'America/Denver': 'US-Mountain',
    'America/Los_Angeles': 'US-West',
    'America/Toronto': 'Canada-East',
    'America/Vancouver': 'Canada-West',
    
    // Europe
    'Europe/London': 'UK',
    'Europe/Paris': 'EU-Central',
    'Europe/Berlin': 'EU-Central',
    'Europe/Madrid': 'EU-South',
    'Europe/Rome': 'EU-South',
    'Europe/Stockholm': 'EU-North',
    
    // Asia
    'Asia/Kolkata': 'India',
    'Asia/Tokyo': 'Japan',
    'Asia/Shanghai': 'China',
    'Asia/Singapore': 'Southeast-Asia',
    'Asia/Dubai': 'UAE',
    
    // Oceania
    'Australia/Sydney': 'Australia-East',
    'Australia/Perth': 'Australia-West',
    'Pacific/Auckland': 'New-Zealand',
    
    // South America
    'America/Sao_Paulo': 'Brazil',
    'America/Buenos_Aires': 'Argentina',
    
    // Africa
    'Africa/Johannesburg': 'South-Africa',
    'Africa/Cairo': 'North-Africa',
  };
  
  // Try to find an exact match
  if (timezoneToRegion[timezone]) {
    return timezoneToRegion[timezone];
  }
  
  // If no exact match, try to find a partial match based on the continent
  const continent = timezone.split('/')[0];
  const continentDefaults = {
    'America': 'US-East',
    'Europe': 'EU-Central',
    'Asia': 'India',
    'Australia': 'Australia-East',
    'Pacific': 'New-Zealand',
    'Africa': 'South-Africa',
  };
  
  return continentDefaults[continent] || 'UK'; // Default to UK if no match found
};
export const combineDateAndTime = (date_, hours, minutes, timezone = 'UTC') => {
  if (!date_) return "";

  // Parse and validate hours and minutes with defaults
  const validHours = parseInt(hours) || 0;
  const validMinutes = parseInt(minutes) || 0;

  // Extract the 'YYYY-MM-DD' part without timezone shifting
  // If date_ is a string from server (ISO), we should use UTC to get the intended date.
  // If date_ is a Date object from the picker, we should use local to get the intended date.
  let dateOnly;
  if (typeof date_ === 'string') {
    dateOnly = dayjs.utc(date_).format('YYYY-MM-DD');
  } else {
    dateOnly = dayjs(date_).format('YYYY-MM-DD');
  }
  
  // Create a new date with the specified time in the given timezone
  const combinedDateTime = dayjs.tz(`${dateOnly} ${validHours.toString().padStart(2, '0')}:${validMinutes.toString().padStart(2, '0')}`, timezone);

  // Format the date in the required format
  const formattedDate = combinedDateTime.format("YYYY-MM-DDTHH:mm:ss.SSSZ");

  return formattedDate;
};

export const formatDateFromDateAndTime = (dateString, timezone = 'UTC') => {
  // Parse the date string with timezone
  const date = dayjs.utc(dateString).tz(timezone);

  // Format the date as needed
  return date.format("MMMM D, YYYY h:mm A"); // Example format: April 3, 2023 10:36 PM
};

export const getDateTimeFromDate = (dateString, timezone = 'UTC') => {
  const parsedDate = dayjs(dateString).tz(timezone);

  // Extract date (YYYY-MM-DD)
  const date = dayjs.utc(dateString).format("YYYY-MM-DD");

  // Extract hours (HH)
  const hours = parsedDate.format("HH");

  // Extract minutes (mm)
  const minutes = parsedDate.format("mm");

  return { date, hours: Number(hours), minutes: Number(minutes) };
};

export const formatDateTimeRange = (startISO, endISO, lastEntryISO, region = null) => {
  // Get timezone based on region or use UTC as default
  const timezone = region ? getTimezoneFromRegion(region) : 'UTC';
  
  // Create date objects in the specified timezone
  const startDateObj = dayjs(startISO).tz(timezone);
  const endDateObj = dayjs(endISO).tz(timezone);
  const lastEntryDateObj = lastEntryISO ? dayjs(lastEntryISO).tz(timezone) : null;
  
  // Function to ensure two-digit formatting
  const formatTwoDigits = (num) => num.toString().padStart(2, "0");

  // Extract Date in the specified timezone
  const startDate = startDateObj.format("YYYY-MM-DD");
  const endDate = endDateObj.format("YYYY-MM-DD");
  const lastEntryDate = lastEntryDateObj ? lastEntryDateObj.format("YYYY-MM-DD") : null;
  
  // Extract Hours and Minutes in the specified timezone
  const startHours = formatTwoDigits(startDateObj.hour());
  const startMinutes = formatTwoDigits(startDateObj.minute());
  const endHours = formatTwoDigits(endDateObj.hour());
  const endMinutes = formatTwoDigits(endDateObj.minute());
  
  // Extract last entry Hours and Minutes if available
  const lastHours = lastEntryDateObj ? formatTwoDigits(lastEntryDateObj.hour()) : null;
  const lastMinutes = lastEntryDateObj ? formatTwoDigits(lastEntryDateObj.minute()) : null;
  
  return {
      startHours: { label: startHours, value: startHours },
      startMinutes: { label: startMinutes, value: startMinutes },
      endHours: { label: endHours, value: endHours },
      endMinutes: { label: endMinutes, value: endMinutes },
      lastHours: lastHours ? { label: lastHours, value: lastHours } : null,
      lastMinutes: lastMinutes ? { label: lastMinutes, value: lastMinutes } : null,
      on_sale_from: startDate,
      on_sale_to: endDate,
      last_entry_date: lastEntryDate,
      timezone: timezone  // Include the timezone in the returned object for reference
  };
};

export function debounce(func, timeout = 300) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export function throttle(func, timeout = 300) {
  let timer = null;
  return (...args) => {
    if (!timer) {
      func(...args);
      timer = setTimeout(() => {
        timer = null;
      }, timeout);
    }
  };
}

export const requestHandler = async (request, showMessage = true) => {
  let loadingToast = null;

  try {
    if (showMessage) {
      // Show loading toast
      loadingToast = Toast().Loading("Loading, please wait...");
    }

    // Make the request (assume `request()` returns a Promise)
    const result = await request();

    // If a message needs to be shown
    if (showMessage) {
      // Show success and update the loading toast using `id`
      Toast().Success(result?.data?.message || "Success", {
        id: loadingToast,
      });
    }

    return result;
  } catch (error) {
    // On error, update the loading toast to error state using `id`
    if (showMessage) {
      Toast().Error(
        error?.response?.data?.message ||
          error?.response?.data?.errors?.[0]?.msg ||
          "Error occurred",
        {
          id: loadingToast,
        }
      );
    }
    // Always throw the error so calling code can handle it in catch blocks
    throw error;
  }
};

export const isToday = (date, timezone = 'UTC') => {
  return dayjs(date).tz(timezone).isSame(dayjs().tz(timezone), "day");
};

export const getDate = (date, timezone = 'UTC') => {
  const dateObject = dayjs(date).tz(timezone);
  const formattedDate = dateObject.format("MM/DD/YYYY");
  return formattedDate;
};

export const QuillToolbar = () => (
  <div id="toolbar">
    <span className="ql-formats">
      <select className="ql-font" defaultValue="arial">
        <option value="arial">Arial</option>
        <option value="comic-sans">Comic Sans</option>
        <option value="courier-new">Courier New</option>
        <option value="georgia">Georgia</option>
        <option value="helvetica">Helvetica</option>
        <option value="lucida">Lucida</option>
      </select>
      <select className="ql-size" defaultValue="medium">
        <option value="extra-small">Size 1</option>
        <option value="small">Size 2</option>
        <option value="medium">Size 3</option>
        <option value="large">Size 4</option>
      </select>
      <select className="ql-header" defaultValue="3">
        <option value="1">Heading</option>
        <option value="2">Subheading</option>
        <option value="3">Normal</option>
      </select>
    </span>
    <span className="ql-formats">
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
      <button className="ql-strike" />
    </span>
    <span className="ql-formats">
      <button className="ql-list" value="ordered" />
      <button className="ql-list" value="bullet" />
      <button className="ql-indent" value="-1" />
      <button className="ql-indent" value="+1" />
    </span>
    <span className="ql-formats">
      <button className="ql-script" value="super" />
      <button className="ql-script" value="sub" />
      <button className="ql-blockquote" />
      <button className="ql-direction" />
    </span>
    <span className="ql-formats">
      <select className="ql-align" />
      <select className="ql-color" />
      <select className="ql-background" />
    </span>
    <span className="ql-formats">
      <button className="ql-link" />
      <button className="ql-image" />
      <button className="ql-video" />
    </span>
    <span className="ql-formats">
      <button className="ql-formula" />
      <button className="ql-code-block" />
      <button className="ql-clean" />
    </span>
    <span className="ql-formats">
      <button className="ql-undo">{/* <CustomUndo /> */}</button>
      <button className="ql-redo">{/* <CustomRedo /> */}</button>
    </span>
  </div>
);

export const sssdc = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      // undo: undoChange,
      // redo: redoChange
    },
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

export const allowOnlyAlphaAndSpace = (e) => {
  return e.target.value.replace(/[^a-zA-z\s]+$/g, "");
};

export const groupedData = (data) => {
  if (!data) return [];
  
  const groupedArray = {};
  
  data.forEach(ele => {
    if (!ele.variants) return; // Skip items with null variants
    
    if (groupedArray[ele.id]) {
      // Merge variants if entry already exists and both have variants
      groupedArray[ele.id].variants = [
        ...(groupedArray[ele.id].variants || []),
        ...ele.variants
      ].filter(Boolean); // Remove any null values
    } else {
      // Add new entry
      groupedArray[ele.id] = {
        ...ele,
        variants: [...(ele.variants || [])]
      };
    }
  });

  return Object.values(groupedArray);
};